import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  FileText,
  User,
  Loader2,
  BarChart3,
  History
} from 'lucide-react';

interface Message {
  role: string;
  content: string;
  timestamp: string;
}

interface ConversationHistory {
  session_id: string;
  patient_id: string;
  patient_name: string;
  conversation_history: Message[];
  model_name: string;
  created_at: string;
  updated_at: string;
}

const SessionReviewPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [conversation, setConversation] = useState<ConversationHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConversationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchConversationHistory = async () => {
    try {
      const response = await apiClient.get(`/api/conversation/history/${sessionId}`);
      setConversation(response.data);
    } catch (error: any) {
      console.error('Failed to fetch conversation:', error);
      setError(error.response?.data?.detail || 'Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToDashboard = async () => {
    try {
      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else {
        navigate('/patient-chart/patient_0');
      }
    } catch (err) {
      navigate('/patient-chart/patient_0');
    }
  };

  const handleBackToEvaluation = () => {
    navigate(`/evaluation/${sessionId}`);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-muted-foreground">Loading conversation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-red-700 mb-2">Error Loading Conversation</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={handleReturnToDashboard}>Return to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToEvaluation}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">Session Review - Conversation Transcript</h1>
          <div className="flex-1"></div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/history')}
            className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">My History</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-4xl">
        {conversation && (
          <>
            {/* Session Info Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">{conversation.patient_name}</h2>
                    <p className="text-sm text-muted-foreground">Session ID: {conversation.session_id}</p>
                    <p className="text-sm text-muted-foreground">
                      Started: {new Date(conversation.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-primary text-primary-foreground">
                      <FileText className="h-3 w-3 mr-1" />
                      {conversation.conversation_history.length} messages
                    </Badge>
                    <Badge variant="outline">
                      <User className="h-3 w-3 mr-1" />
                      {conversation.model_name}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conversation Transcript */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Conversation Transcript</h3>
                <Separator className="mb-6" />

                <div className="space-y-4">
                  {conversation.conversation_history.map((message, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-l-4 ${
                        message.role === 'user'
                          ? 'bg-blue-50 border-l-blue-600 dark:bg-blue-950'
                          : 'bg-gray-50 border-l-gray-600 dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold ${
                          message.role === 'user' ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          {message.role === 'user' ? 'You (Trainee)' : conversation.patient_name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm">{message.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" size="lg" onClick={handleReturnToDashboard}>
                Return to Study Dashboard
              </Button>
              <Button size="lg" onClick={handleBackToEvaluation}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Back to Evaluation
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionReviewPage;
